import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { QrCode, Package, ClipboardList, AlertTriangle, Factory, Warehouse, Clock, TrendingUp, RefreshCw } from 'lucide-react';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

export default function MobileDashboard() {
  const navigate = useNavigate();
  const [workOrders, setWorkOrders] = useState([]);
  const [warehouseData, setWarehouseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [woRes, whRes] = await Promise.all([
        axios.get(`${API}/work-orders`),
        axios.get(`${API}/warehouse/dashboard`).catch(() => ({ data: null })),
      ]);
      setWorkOrders(woRes.data || []);
      setWarehouseData(whRes.data);
    } catch (err) {
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const today = format(new Date(), 'yyyy-MM-dd');
  const todayWOs = workOrders.filter(wo => wo.planned_date === today);
  const inProduction = workOrders.filter(wo => wo.status === 'in_production');
  const totalPalletsToday = todayWOs.reduce((sum, wo) => sum + (wo.pallets_completed || 0), 0);
  const utilization = warehouseData?.overall_utilization || 0;
  const holdsCount = warehouseData?.pallets_on_hold || 0;

  if (loading) {
    return (
      <div className="mobile-page">
        <div className="mobile-loading"><div className="mobile-spinner" /></div>
      </div>
    );
  }

  return (
    <div className="mobile-page">
      {/* Greeting */}
      <div style={{ marginBottom: 16 }}>
        <div className="mobile-page-title">Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 17 ? 'afternoon' : 'evening'}</div>
        <div className="mobile-page-subtitle">{format(new Date(), 'EEEE, MMMM d, yyyy')}</div>
      </div>

      {/* Refresh */}
      <button
        onClick={handleRefresh}
        style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#007aff', background: 'none', border: 'none', marginBottom: 12, cursor: 'pointer' }}
      >
        <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
        {refreshing ? 'Refreshing...' : 'Refresh'}
      </button>

      {/* KPIs */}
      <div className="mobile-kpi-row">
        <div className="mobile-kpi-card blue">
          <div className="mobile-kpi-label">WOs Today</div>
          <div className="mobile-kpi-value">{todayWOs.length}</div>
          <div className="mobile-kpi-detail">{todayWOs.filter(w => w.status === 'completed' || w.status === 'finalized').length} completed</div>
        </div>
        <div className="mobile-kpi-card green">
          <div className="mobile-kpi-label">In Production</div>
          <div className="mobile-kpi-value">{inProduction.length}</div>
          <div className="mobile-kpi-detail">Active lines</div>
        </div>
        <div className="mobile-kpi-card orange">
          <div className="mobile-kpi-label">Pallets Today</div>
          <div className="mobile-kpi-value">{totalPalletsToday}</div>
          <div className="mobile-kpi-detail">Logged this shift</div>
        </div>
        <div className="mobile-kpi-card purple">
          <div className="mobile-kpi-label">Warehouse</div>
          <div className="mobile-kpi-value">{utilization.toFixed(1)}%</div>
          <div className="mobile-kpi-detail">{holdsCount} on hold</div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mobile-section-header">
        <span className="mobile-section-title">Quick Actions</span>
      </div>
      <div className="mobile-quick-actions">
        <button className="mobile-quick-action" onClick={() => navigate('/mobile/warehouse/scan')}>
          <div className="mobile-quick-action-icon blue"><QrCode size={20} /></div>
          <span className="mobile-quick-action-label">Scan LP</span>
        </button>
        <button className="mobile-quick-action" onClick={() => navigate('/mobile/warehouse/receive')}>
          <div className="mobile-quick-action-icon green"><Package size={20} /></div>
          <span className="mobile-quick-action-label">Receive</span>
        </button>
        <button className="mobile-quick-action" onClick={() => navigate('/mobile/production')}>
          <div className="mobile-quick-action-icon orange"><ClipboardList size={20} /></div>
          <span className="mobile-quick-action-label">Log Pallet</span>
        </button>
        <button className="mobile-quick-action" onClick={() => navigate('/mobile/warehouse/holds')}>
          <div className="mobile-quick-action-icon red"><AlertTriangle size={20} /></div>
          <span className="mobile-quick-action-label">Holds</span>
        </button>
      </div>

      {/* Active Production */}
      <div className="mobile-section-header">
        <span className="mobile-section-title">Active Production</span>
        <button className="mobile-section-action" onClick={() => navigate('/mobile/production')}>View All</button>
      </div>
      {inProduction.length === 0 ? (
        <div className="mobile-empty-state">
          <Factory />
          <p>No active production lines</p>
        </div>
      ) : (
        inProduction.slice(0, 3).map(wo => {
          const progress = wo.planned_quantity > 0 ? ((wo.actual_quantity || 0) / wo.planned_quantity) * 100 : 0;
          return (
            <div
              key={wo.wo_id}
              className="mobile-wo-card status-in_production"
              onClick={() => navigate(`/mobile/production/${wo.wo_id}`)}
            >
              <div className="mobile-wo-header">
                <div>
                  <div className="mobile-wo-number">{wo.wo_number} · Line {wo.production_line}</div>
                  <div className="mobile-wo-item-name">{wo.item_name}</div>
                </div>
                <span className="mobile-status-badge in_production">Running</span>
              </div>
              <div className="mobile-wo-meta">
                <span className="mobile-wo-meta-item">
                  <Package size={14} />
                  {wo.actual_quantity || 0} / {wo.planned_quantity} cases
                </span>
                <span className="mobile-wo-meta-item">
                  <ClipboardList size={14} />
                  {wo.pallets_completed || 0} pallets
                </span>
              </div>
              <div className="mobile-wo-progress">
                <div className="mobile-wo-progress-bar">
                  <div className="mobile-wo-progress-fill green" style={{ width: `${Math.min(progress, 100)}%` }} />
                </div>
                <div className="mobile-wo-progress-text">
                  <span>{progress.toFixed(0)}% complete</span>
                  <span>{wo.actual_quantity || 0} / {wo.planned_quantity}</span>
                </div>
              </div>
            </div>
          );
        })
      )}

      {/* Today's Schedule Preview */}
      <div className="mobile-section-header" style={{ marginTop: 8 }}>
        <span className="mobile-section-title">Today's Schedule</span>
        <button className="mobile-section-action" onClick={() => navigate('/mobile/schedule')}>Calendar</button>
      </div>
      {todayWOs.filter(w => w.status === 'scheduled' || w.status === 'staged').length === 0 ? (
        <div className="mobile-empty-state">
          <Clock />
          <p>No upcoming work orders today</p>
        </div>
      ) : (
        todayWOs.filter(w => w.status === 'scheduled' || w.status === 'staged').slice(0, 3).map(wo => (
          <div
            key={wo.wo_id}
            className={`mobile-wo-card status-${wo.status}`}
            onClick={() => navigate(`/mobile/production/${wo.wo_id}`)}
          >
            <div className="mobile-wo-header">
              <div>
                <div className="mobile-wo-number">{wo.wo_number} · Line {wo.production_line}</div>
                <div className="mobile-wo-item-name">{wo.item_name}</div>
              </div>
              <span className={`mobile-status-badge ${wo.status}`}>
                {wo.status === 'staged' ? 'Staged' : 'Scheduled'}
              </span>
            </div>
            <div className="mobile-wo-meta">
              <span className="mobile-wo-meta-item">
                <Clock size={14} />
                {wo.planned_start_time || 'TBD'} - {wo.planned_end_time || 'TBD'}
              </span>
              <span className="mobile-wo-meta-item">
                <Package size={14} />
                {wo.planned_quantity} cases planned
              </span>
            </div>
          </div>
        ))
      )}
    </div>
  );
}
